"use client";

import React, { useEffect } from "react";
import { Flex } from "@ecco/ui/flex";
import { Text } from "@ecco/ui/text";
import { space } from "@ecco/ui/tokens";
import { NextImage } from "@ecco/ui/next-image";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Checkout, CommonCMS } from "@lib/constants/contentful";
import type { TAmazonPayHeapMethod } from "@features/checkout/types";
import { HeapAmazonPayButton } from "./HeapAmazonPayButton";
import { HEAP_AMAZON_PAY_CONTEXT_COOKIE } from "@lib/utils/checkout-utility/amazon-pay/constants";
import { savePurchaseSnapshot } from "@lib/utils/checkout-utility/amazon-pay/purchaseSnapshot";
import type { UtmModel } from "@interfaces/heap-api";
import type { Cart } from "@graphql/generated/types";

type HeapAmazonPayPaymentOptionProps = {
    method: TAmazonPayHeapMethod;
    cart: Cart;
    cartId: string;
    locale: string;
    isSubscribed?: boolean;
    utm: UtmModel;
};

export const HeapAmazonPayPaymentOption = ({
    method,
    cart,
    cartId,
    locale,
    isSubscribed,
    utm,
}: HeapAmazonPayPaymentOptionProps) => {
    const iconDimensions = {
        width: parseInt(space[5].replace("px", "")),
        height: parseInt(space[5].replace("px", "")),
    };
    const { get } = useMicrocopy();
    const disclaimerText = get(CommonCMS.page, Checkout.amazonPay.disclaimer);

    // Persist the checkout context for the return route handler. The cookie must outlive
    // the redirect to Amazon and back, hence sameSite=lax.
    useEffect(() => {
        const value = encodeURIComponent(
            JSON.stringify({ cartId, locale, isSubscribed: !!isSubscribed, utm })
        );
        // Mark Secure on HTTPS so the cookie isn't sent over a plain HTTP fallback.
        // The cookie carries only non-sensitive context (cartId, locale, isSubscribed, utm) —
        // never tokens or credentials — but defense-in-depth is cheap.
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `${HEAP_AMAZON_PAY_CONTEXT_COOKIE}=${value}; path=/; samesite=lax${secure}`;
    }, [cartId, locale, isSubscribed, utm]);

    // The order is placed server-side on return from Amazon, so no client code runs at
    // authorisation time to report the purchase. Stash the cart for the confirmation page,
    // which is the first client code to run afterwards.
    useEffect(() => {
        savePurchaseSnapshot(cart);
    }, [cart]);

    return (
        <div className="adyen-checkout__payment-method adyen-checkout__payment-method--amazonpay adyen-checkout__payment-method--selected">
            <div className="adyen-checkout__payment-method__header">
                <span className="adyen-checkout__payment-method__header__title">
                    <span className="adyen-checkout__payment-method__image__wrapper adyen-checkout__payment-method__image__wrapper--outline">
                        <NextImage
                            unoptimized
                            src="/assets/icons/amazonpay-logo.svg"
                            alt="Amazon Pay"
                            className="adyen-checkout__payment-method__image adyen-checkout__image adyen-checkout__image--loaded"
                            width={iconDimensions.width}
                            height={iconDimensions.height}
                        />
                    </span>
                    <span className="adyen-checkout__payment-method__name_wrapper">
                        <span className="adyen-checkout__payment-method__name">
                            <Flex gap="1" direction="column" display="inline-flex">
                                <Text>Amazon Pay</Text>
                                {disclaimerText && (
                                    <Text color="gray.500" fontSize="xs">
                                        {disclaimerText}
                                    </Text>
                                )}
                            </Flex>
                        </span>
                    </span>
                </span>
            </div>
            <div className="adyen-checkout-pm-details-wrapper" aria-hidden="false">
                <div className="adyen-checkout__payment-method__details">
                    <div className="adyen-checkout__payment-method__details__content">
                        <HeapAmazonPayButton method={method} width="250px" />
                    </div>
                </div>
            </div>
        </div>
    );
};
